/* global Theme */
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
import { fetchConfig } from '@theme/utilities';
import { GIFT_PROPERTY, GIFT_PROPERTY_VALUE, isGiftLine } from '@bbd/cart-rules';

/**
 * Gift mode for the theme's quick-add modal (PIG-320). Multi variant gifts in the gift picker open the
 * quick-add modal. The item that gets added must carry the Monk gift property, and the gift that was
 * chosen before has to be gone before the new one is added, otherwise the cart shows two gifts for a moment.
 * The base quick-add.js is untouched.
 */

/** Tells the gift picker to show its loading state, see assets/bbd-gift-picker.js */
const GIFT_BUSY_EVENT = 'bbd-gift:busy';

const setPickerBusy = (/** @type {boolean} */ busy) => {
  document.dispatchEvent(new CustomEvent(GIFT_BUSY_EVENT, { detail: { busy } }));
};

/** A failed add (offline, a server error) gives no cart event. After this long the picker is freed again. */
const ADD_TIMEOUT = 15000;

let giftMode = false;
/** @type {ReturnType<typeof setTimeout> | undefined} */
let addTimer;
/** Whether the picker was told to show its loading state */
let pickerBusy = false;
/** Whether we removed the previous gift and are waiting for the add to finish */
let removedPreviousGift = false;
/** @type {WeakSet<HTMLFormElement>} Forms that may be submitted for real, we already removed the old gift */
const readyForms = new WeakSet();

// Capture phase: runs before the quick-add component handles the click
document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-gift-choose]')) {
      giftMode = true;
      return;
    }

    // Any other quick-add button (product cards) is a normal add
    if (event.target.closest('quick-add-component')) giftMode = false;
  },
  true
);

document.addEventListener(DialogCloseEvent.eventName, () => {
  giftMode = false;
});

/**
 * The add did not finish: show the picker again, and when the previous gift was already removed refresh the cart view,
 * otherwise it still shows the gift that is gone.
 */
const recoverFromFailedAdd = () => {
  if (!pickerBusy) return;

  pickerBusy = false;
  setPickerBusy(false);

  if (removedPreviousGift) {
    removedPreviousGift = false;
    document.dispatchEvent(new CartUpdateEvent({}, 'bbd-quick-add-gift', { source: 'bbd-quick-add-gift' }));
  }
};

/**
 * Removes the gift lines that are in the cart, without refreshing the cart view.
 */
const removeGiftLines = async () => {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' });
  const cart = await response.json();
  const gifts = cart.items.filter(isGiftLine);

  for (const gift of gifts) {
    await fetch(
      Theme.routes.cart_change_url,
      fetchConfig('json', { body: JSON.stringify({ id: gift.key, quantity: 0 }) })
    );
  }

  return gifts.length > 0;
};

// Remove the previous gift first, then let the add go through. The cart view is only refreshed by the add,
// so it goes from the old gift to the new one in one step.
// On window, not document: the theme handles submit with delegated listeners on document, a document listener
// of ours would run after them and the add would already be on its way.
window.addEventListener(
  'submit',
  async (event) => {
    if (!giftMode) return;
    if (!(event.target instanceof HTMLFormElement) || !event.target.closest('#quick-add-dialog')) return;

    const form = event.target;
    if (readyForms.has(form)) {
      readyForms.delete(form);
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    pickerBusy = true;
    setPickerBusy(true);

    try {
      removedPreviousGift = await removeGiftLines();
    } catch (error) {
      console.error('BBD quick-add gift mode could not remove the previous gift:', error);
    }

    readyForms.add(form);
    form.requestSubmit();

    clearTimeout(addTimer);
    addTimer = setTimeout(recoverFromFailedAdd, ADD_TIMEOUT);
  },
  true
);

// FormData is built from the form when it is submitted, add the gift property to it
document.addEventListener('formdata', (event) => {
  if (!giftMode) return;
  if (!(event.target instanceof Element) || !event.target.closest('#quick-add-dialog')) return;

  event.formData.set(`properties[${GIFT_PROPERTY}]`, GIFT_PROPERTY_VALUE);
  event.formData.set('quantity', '1');
});

// If the add failed after the old gift was removed, the cart view still shows the old gift: refresh it
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  clearTimeout(addTimer);

  if (pickerBusy) {
    pickerBusy = false;
    setPickerBusy(false);
  }

  if (!removedPreviousGift) return;
  removedPreviousGift = false;

  const detail = /** @type {CustomEvent} */ (event).detail;
  if (!detail?.data?.didError) return;

  document.dispatchEvent(new CartUpdateEvent({}, 'bbd-quick-add-gift', { source: 'bbd-quick-add-gift' }));
});
